import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';

import {
  useGetHarvestSettings,
  usePatchHarvestSettings,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import type { HarvestSettings, JsonPatchOperation } from '../../api/generated/model';

const empty: HarvestSettings = {
  oidc_audience: '',
  gemini_api_key: '',
  prefilter_floor_epoch: 0,
  watermark_buffer_seconds: 259200,
  internal_domains: '',
  label_batch_limit: 200,
  triage_model: '',
  verify_model: '',
  extract_model: '',
};

// The floor is stored as a unix-second epoch but presented as a plain date. 0
// (or blank) means "no floor — scan all history".
const epochToDate = (epoch: number): string =>
  epoch > 0 ? new Date(epoch * 1000).toISOString().slice(0, 10) : '';
const dateToEpoch = (s: string): number => (s ? Math.floor(Date.parse(`${s}T00:00:00Z`) / 1000) : 0);

const HarvestSettingsTab: React.FC = () => {
  const { data, isLoading, refetch } = useGetHarvestSettings();
  const patchMutation = usePatchHarvestSettings();

  const [form, setForm] = useState<HarvestSettings>(empty);
  const [original, setOriginal] = useState<HarvestSettings>(empty);
  // The date input is backed by its own string so typing isn't disrupted by a
  // round-trip through the epoch (the native picker mangles partial entries).
  const [floorDate, setFloorDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ variant: string; text: string } | null>(null);

  useEffect(() => {
    if (data) {
      setForm(data);
      setOriginal(data);
      setFloorDate(epochToDate(data.prefilter_floor_epoch ?? 0));
    }
  }, [data]);

  const hasChanges = JSON.stringify(form) !== JSON.stringify(original);
  const set = (k: keyof HarvestSettings, v: string | number) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      // Send every field as an upsert ('add' creates-or-replaces), so no field
      // can be silently skipped — this is a small singleton, so it's cheap.
      const patches: JsonPatchOperation[] = (Object.keys(form) as (keyof HarvestSettings)[])
        .map((k) => ({ op: 'add', path: `/${k}`, value: form[k] }));
      await patchMutation.mutateAsync({ data: patches });
      await refetch();
      setMsg({ variant: 'success', text: 'Settings saved.' });
    } catch (err: any) {
      setMsg({ variant: 'danger', text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const textField = (label: string, k: keyof HarvestSettings, help?: string, type: string = 'text') => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control type={type} value={String(form[k] ?? '')} onChange={(e) => set(k, e.target.value)} />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  const numField = (label: string, k: keyof HarvestSettings, help?: string) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control type="number" value={Number(form[k] ?? 0)} onChange={(e) => set(k, parseInt(e.target.value || '0', 10))} />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  if (isLoading) return <Spinner animation="border" />;

  return (
    <Card>
      <Card.Body>
        {msg && <Alert variant={msg.variant} dismissible onClose={() => setMsg(null)}>{msg.text}</Alert>}

        <h6 className="text-muted">Forwarder authentication &amp; classification</h6>
        {textField('Forwarder OAuth client ID', 'oidc_audience', "The Apps Script project's OAuth client id. The forwarder's identity token is verified against this — empty means all forwarder requests are rejected.")}
        {textField('Gemini API key', 'gemini_api_key', 'Used for triage/verify/extract classification. Empty = classification is blocked.', 'password')}
        {textField('Internal sender domains', 'internal_domains', 'Comma-separated staff domains (e.g. arrayscape.com) whose senders are never treated as applicants.')}

        <hr />
        <h6 className="text-muted">Scan</h6>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Scan emails received on or after</Form.Label>
              <Form.Control
                type="date"
                value={floorDate}
                onChange={(e) => { setFloorDate(e.target.value); set('prefilter_floor_epoch', dateToEpoch(e.target.value)); }}
              />
              <Form.Text muted>The oldest email to harvest back to. Leave blank to scan all history.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            {numField('Max labels per scan', 'label_batch_limit', 'How many job-application messages the forwarder is told to label per scan cycle.')}
          </Col>
        </Row>

        <hr />
        <h6 className="text-muted">Gemini models</h6>
        <Row>
          <Col md={4}>{textField('Triage model', 'triage_model')}</Col>
          <Col md={4}>{textField('Verify model', 'verify_model')}</Col>
          <Col md={4}>{textField('Extract model', 'extract_model')}</Col>
        </Row>

        <div className="d-flex justify-content-end pt-2 border-top">
          <Button variant="outline-secondary" className="me-2" disabled={!hasChanges || saving} onClick={() => { setForm(original); setFloorDate(epochToDate(original.prefilter_floor_epoch ?? 0)); }}>Cancel</Button>
          <Button variant="primary" disabled={!hasChanges || saving} onClick={save}>
            {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</> : 'Save'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default HarvestSettingsTab;
